import _ from 'lodash'
import React from 'react'
import { Input } from 'semantic-ui-react'
import classnames from 'classnames'
import { formFieldWrapper } from '../formFieldWrapper'
import { injectPropsIntoInput } from './injectPropsIntoInput'
import { getThemedCompact } from '../util'
import { useApplicationTheme } from '../../applicationTheme'
import { FormFieldCommonProps, Theme } from '../../../types'

export interface InputFieldProps extends FormFieldCommonProps {
  inputLabel?: string
  normalizeOnBlur?: (value: string) => string
  normalizeOnChange?: (value: string) => string
  compact?: boolean
  fluid?: boolean
  guide?: React.ReactNode
  value?: string | number | undefined
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  placeholder?: string
  className?: string
  showError?: boolean
  children?: React.ReactNode
  action?: React.ReactNode
  theme?: Theme
}

const BaseInputField: React.FC<InputFieldProps> = ({
  elmId,
  value,
  onChange,
  onBlur,
  placeholder,
  inputLabel,
  normalizeOnBlur,
  normalizeOnChange,
  showError,
  fluid = true,
  compact,
  className,
  guide, // eslint-disable-line @typescript-eslint/no-unused-vars
  children,
  action,
  ...rest
}) => {
  const theme = useApplicationTheme()
  const newCompact = getThemedCompact(theme, compact)

  const onChangeNew = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (normalizeOnChange) {
      e.target.value = normalizeOnChange(e.target.value)
    }
    if (onChange) {
      onChange(e)
    }
  }

  const onBlurNew = (e: React.FocusEvent<HTMLInputElement>) => {
    if (normalizeOnBlur) {
      e.target.value = normalizeOnBlur(e.target.value)
    }
    if (onBlur) {
      onBlur(e)
    }
  }

  const newClassName = classnames({
    compact: newCompact,
    [className || '']: !!className,
  })

  const finalValue = _.isNil(value) ? '' : String(value)

  const inputProps = {
    onChange: onChangeNew,
    value: finalValue,
    onBlur: onBlurNew,
    placeholder,
    // @ts-ignore next
    // eslint-disable-next-line
    disabled: rest.disabled,
  }

  // Prepare the input element
  let inputElement: React.ReactNode
  if (children) {
    // eslint-disable-next-line
    inputElement = injectPropsIntoInput(children, inputProps)
  } else {
    inputElement = <input {...inputProps} />
  }

  return (
    <Input
      aria-label={`${elmId}_input`}
      data-elm-id={`${elmId}_input`}
      className={newClassName}
      onChange={onChangeNew}
      value={finalValue}
      onBlur={onBlurNew}
      error={showError}
      placeholder={placeholder}
      label={inputLabel}
      fluid={fluid}
      {..._.omit(rest, ['subLabel'])}
    >
      {action || null}
      {inputElement}
    </Input>
  )
}

export default formFieldWrapper(
  (fieldProps: InputFieldProps, showError?: boolean) => (
    <BaseInputField {...fieldProps} showError={showError} />
  )
)
